/**
 * Form Validation Utilities
 * Hard validations for invoice/quotation forms
 * 2026 UX Standard: Calm, contextual, action-oriented
 */

/**
 * Validate invoice/quotation before sending
 * @param {object} data - Invoice or quotation data
 * @returns {object} { isValid: boolean, errors: object, warnings: object, canSend: boolean }
 */
export function validateInvoiceForm(data) {
  const errors = {}; // Blocking errors
  const warnings = {}; // Non-blocking warnings

  // Client validation (BLOCKING)
  if (!data.client || !data.client.id) {
    errors.client = {
      message: "Select a client to continue",
      field: "client",
      severity: "error",
    };
  }

  // Items validation (BLOCKING)
  if (!data.items || data.items.length === 0) {
    errors.items = {
      message: "Add at least one line item",
      field: "items",
      severity: "error",
    };
  } else {
    // Validate each item
    const itemErrors = [];
    data.items.forEach((item, index) => {
      const itemError = {};

      if (!item.name || item.name.trim() === "") {
        itemError.name = "Item name is required";
      }

      if (!item.quantity || item.quantity <= 0) {
        itemError.quantity = "Quantity must be greater than 0";
      }

      if (item.rate === undefined || item.rate === null || item.rate < 0) {
        itemError.rate = "Rate must be 0 or greater";
      }

      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });

    if (itemErrors.length > 0) {
      errors.itemErrors = {
        message: "Fix item errors before sending",
        details: itemErrors,
        field: "items",
        severity: "error",
      };
    }
  }

  // Date validations (BLOCKING)
  if (!data.issueDate && !data.quoteDate) {
    errors.date = {
      message: "Issue date is required",
      field: "issueDate",
      severity: "error",
    };
  }

  if (data.issueDate && data.dueDate) {
    const issueDate = new Date(data.issueDate);
    const dueDate = new Date(data.dueDate);
    
    if (dueDate < issueDate) {
      errors.dueDate = {
        message: "Due date cannot be before issue date",
        field: "dueDate",
        severity: "error",
      };
    }
  }

  // GSTIN warning (NON-BLOCKING)
  if (data.client && !data.client.gstin) {
    warnings.gstin = {
      message: "Client GSTIN is missing (optional for some invoices)",
      field: "client",
      severity: "warning",
    };
  }

  // Payment terms warning (NON-BLOCKING)
  if (!data.notes || data.notes.trim() === "") {
    warnings.notes = {
      message: "Consider adding payment instructions or notes",
      field: "notes",
      severity: "warning",
    };
  }

  const hasErrors = Object.keys(errors).length > 0;
  const hasWarnings = Object.keys(warnings).length > 0;

  return {
    isValid: !hasErrors,
    canSend: !hasErrors, // Can send even with warnings
    errors,
    warnings,
    summary: hasErrors 
      ? "This invoice isn't ready to send yet. Fix the items below."
      : hasWarnings
      ? "Invoice is ready to send (with minor suggestions)"
      : "Invoice is ready to send",
  };
}

/**
 * Validate quotation form
 * @param {object} data - Quotation data
 * @returns {object} { isValid: boolean, errors: object, warnings: object, canSend: boolean }
 */
export function validateQuotationForm(data) {
  const errors = {};
  const warnings = {};

  // Client validation (BLOCKING)
  if (!data.client || !data.client.id) {
    errors.client = {
      message: "Select a client to continue",
      field: "client",
      severity: "error",
    };
  }

  // Items validation (BLOCKING)
  if (!data.items || data.items.length === 0) {
    errors.items = {
      message: "Add at least one line item",
      field: "items",
      severity: "error",
    };
  } else {
    // Validate each item
    const itemErrors = [];
    data.items.forEach((item, index) => {
      const itemError = {};

      if (!item.name || item.name.trim() === "") {
        itemError.name = "Item name is required";
      }

      if (!item.quantity || item.quantity <= 0) {
        itemError.quantity = "Quantity must be greater than 0";
      }

      if (item.rate === undefined || item.rate === null || item.rate < 0) {
        itemError.rate = "Rate must be 0 or greater";
      }

      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });

    if (itemErrors.length > 0) {
      errors.itemErrors = {
        message: "Fix item errors before sending",
        details: itemErrors,
        field: "items",
        severity: "error",
      };
    }
  }

  // Date validations (BLOCKING)
  if (!data.quoteDate) {
    errors.date = {
      message: "Quote date is required",
      field: "quoteDate",
      severity: "error",
    };
  }

  if (data.quoteDate && data.validUntil) {
    const quoteDate = new Date(data.quoteDate);
    const validUntil = new Date(data.validUntil);
    
    if (validUntil < quoteDate) {
      errors.validUntil = {
        message: "Valid until date cannot be before quote date",
        field: "validUntil",
        severity: "error",
      };
    }
  }

  // Warnings
  if (data.client && !data.client.gstin) {
    warnings.gstin = {
      message: "Client GSTIN is missing (optional)",
      field: "client",
      severity: "warning",
    };
  }

  const hasErrors = Object.keys(errors).length > 0;
  const hasWarnings = Object.keys(warnings).length > 0;

  return {
    isValid: !hasErrors,
    canSend: !hasErrors,
    errors,
    warnings,
    summary: hasErrors 
      ? "This quotation isn't ready to send yet. Fix the items below."
      : hasWarnings
      ? "Quotation is ready to send (with minor suggestions)"
      : "Quotation is ready to send",
  };
}

/**
 * Validate client form
 * @param {object} data - Client data
 * @returns {object} { isValid: boolean, errors: object }
 */
export function validateClientForm(data) {
  const errors = {};

  // Name is required
  if (!data.name || data.name.trim() === "") {
    errors.name = "Client name is required";
  }

  // Email validation (if provided)
  if (data.email && data.email.trim() !== "") {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      errors.email = "Invalid email format";
    }
  }

  // Phone validation (if provided)
  if (data.phone && data.phone.trim() !== "") {
    const phoneRegex = /^[0-9+\-\s()]{10,}$/;
    if (!phoneRegex.test(data.phone)) {
      errors.phone = "Invalid phone number";
    }
  }

  // GSTIN validation (if provided)
  if (data.gstin && data.gstin.trim() !== "") {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstinRegex.test(data.gstin)) {
      errors.gstin = "Invalid GSTIN format";
    }
  }

  // Pincode validation (if provided)
  if (data.pincode && data.pincode.trim() !== "") {
    const pincodeRegex = /^[0-9]{6}$/;
    if (!pincodeRegex.test(data.pincode)) {
      errors.pincode = "Pincode must be 6 digits";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Get first error message from validation errors
 * @param {object} errors - Validation errors object
 * @returns {string} First error message
 */
export function getFirstError(errors) {
  if (!errors || Object.keys(errors).length === 0) {
    return null;
  }

  // Return first error message
  const firstKey = Object.keys(errors)[0];
  const error = errors[firstKey];
  
  return typeof error === 'string' ? error : error.message;
}

/**
 * Get validation summary for display
 * @param {object} validation - Validation result
 * @returns {object} { type: 'error'|'warning'|'success', message: string, items: array }
 */
export function getValidationSummary(validation) {
  if (!validation) {
    return null;
  }

  const errorCount = Object.keys(validation.errors || {}).length;
  const warningCount = Object.keys(validation.warnings || {}).length;

  if (errorCount > 0) {
    const items = Object.values(validation.errors).map(err => 
      typeof err === 'string' ? err : err.message
    );
    
    return {
      type: 'error',
      message: validation.summary || `${errorCount} issue${errorCount > 1 ? 's' : ''} need${errorCount === 1 ? 's' : ''} attention`,
      items,
    };
  }

  if (warningCount > 0) {
    const items = Object.values(validation.warnings).map(warn => 
      typeof warn === 'string' ? warn : warn.message
    );
    
    return {
      type: 'warning',
      message: validation.summary || `${warningCount} suggestion${warningCount > 1 ? 's' : ''}`,
      items,
    };
  }

  return {
    type: 'success',
    message: 'Ready to send',
    items: [],
  };
}

/**
 * Scroll to first error field
 * @param {object} errors - Validation errors object
 */
export function scrollToFirstError(errors) {
  if (!errors || Object.keys(errors).length === 0) {
    return;
  }

  // Get first error field name
  const firstKey = Object.keys(errors)[0];
  const error = errors[firstKey];
  const fieldName = typeof error === 'object' ? error.field : firstKey;
  
  // Try to find element by name, id, or data-field attribute
  const element = 
    document.querySelector(`[name="${fieldName}"]`) ||
    document.querySelector(`#${fieldName}`) ||
    document.querySelector(`[data-field="${fieldName}"]`) ||
    document.querySelector(`[data-validation-target="${fieldName}"]`);

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    
    // Focus if it's an input
    if (element.tagName === "INPUT" || element.tagName === "TEXTAREA" || element.tagName === "SELECT") {
      setTimeout(() => element.focus(), 300);
    }
  }
}
